<template>
  <section class="jcm-hist">
    <div class="jcm-hist__toolbar">
      <div class="jcm-hist__search">
        <font-awesome-icon :icon="['fas', 'search']" class="jcm-hist__search-icon" />
        <input
          v-model="search"
          type="text"
          class="jcm-hist__search-input"
          placeholder="Search subject or recipient…"
          @keyup.enter="fetchLogs"
        />
      </div>
      <button type="button" class="jcm-hist__refresh" :disabled="isLoading" @click="fetchLogs">
        <font-awesome-icon :icon="['fas', isLoading ? 'spinner' : 'sync-alt']" />
        Refresh
      </button>
    </div>

    <div v-if="isLoading" class="jcm-hist__state">Loading email history…</div>
    <div v-else-if="!logs.length" class="jcm-hist__state">
      No Joint Committee emails have been sent for this partner yet.
    </div>
    <div v-else class="jcm-hist__list">
      <article
        v-for="log in logs"
        :key="log.id"
        class="jcm-hist-card"
        :class="{ 'jcm-hist-card--open': expandedId === log.id }"
      >
        <button type="button" class="jcm-hist-card__head" @click="toggleExpand(log.id)">
          <div class="jcm-hist-card__main">
            <p class="jcm-hist-card__subject">{{ log.subject }}</p>
            <p class="jcm-hist-card__meta">
              {{ formatRecipientsSummary(log) }}
              · Sent by {{ log.owner_name || '—' }}
              · {{ formatDate(log.sent_at || log.created_at) }}
            </p>
          </div>
          <span :class="statusBadgeClass(log.status)">{{ formatStatus(log.status) }}</span>
        </button>

        <div v-if="expandedId === log.id" class="jcm-hist-card__body">
          <div class="jcm-hist-card__block">
            <h4 class="jcm-hist-card__label">Recipients & delivery</h4>
            <ul class="jcm-hist-recipients">
              <li v-for="r in log.recipients" :key="r.id" class="jcm-hist-recipients__item">
                <div>
                  <span class="jcm-hist-recipients__name">{{ r.name || r.email }}</span>
                  <span class="jcm-hist-recipients__email">{{ r.email }}</span>
                </div>
                <span :class="deliveryBadgeClass(r.delivery_status)">
                  {{ formatStatus(r.delivery_status) }}
                </span>
              </li>
            </ul>
            <p v-if="log.last_error" class="jcm-hist-card__error">{{ log.last_error }}</p>
          </div>
          <div class="jcm-hist-card__block">
            <h4 class="jcm-hist-card__label">Message content</h4>
            <div class="jcm-hist-card__content" v-html="log.body_html"></div>
          </div>
        </div>
      </article>
    </div>

    <div v-if="paginatorInfo && paginatorInfo.lastPage > 1" class="jcm-hist__pager">
      <button
        type="button"
        class="jcm-hist__page-btn"
        :disabled="page <= 1 || isLoading"
        @click="goPage(page - 1)"
      >
        Previous
      </button>
      <span class="jcm-hist__page-label">Page {{ page }} of {{ paginatorInfo.lastPage }}</span>
      <button
        type="button"
        class="jcm-hist__page-btn"
        :disabled="page >= paginatorInfo.lastPage || isLoading"
        @click="goPage(page + 1)"
      >
        Next
      </button>
    </div>
  </section>
</template>

<script>
import gql from "graphql-tag";
import { formattedDateWithTimeAmPm } from "../../helpers/utils";

export default {
  name: "JointCommitteeEmailHistory",
  props: {
    partnerToken: {
      type: String,
      required: true,
    },
    refreshKey: {
      type: Number,
      default: 0,
    },
  },
  data: function () {
    return {
      logs: [],
      isLoading: false,
      search: "",
      page: 1,
      first: 10,
      paginatorInfo: null,
      expandedId: null,
    };
  },
  watch: {
    partnerToken: function () {
      this.page = 1;
      this.fetchLogs();
    },
    refreshKey: function () {
      this.page = 1;
      this.fetchLogs();
    },
  },
  created: function () {
    this.fetchLogs();
  },
  methods: {
    formatDate: function (date) {
      return formattedDateWithTimeAmPm(date);
    },
    formatStatus: function (status) {
      if (!status) {
        return "—";
      }
      return String(status).replace(/_/g, " ");
    },
    statusBadgeClass: function (status) {
      var key = String(status || "").toLowerCase();
      if (key === "sent") {
        return "jcm-hist-badge jcm-hist-badge--sent";
      }
      if (key === "failed") {
        return "jcm-hist-badge jcm-hist-badge--failed";
      }
      if (key === "partially_sent") {
        return "jcm-hist-badge jcm-hist-badge--partial";
      }
      return "jcm-hist-badge jcm-hist-badge--queued";
    },
    deliveryBadgeClass: function (status) {
      var key = String(status || "").toLowerCase();
      if (key === "sent") {
        return "jcm-hist-badge jcm-hist-badge--sent";
      }
      if (key === "failed" || key === "bounced") {
        return "jcm-hist-badge jcm-hist-badge--failed";
      }
      return "jcm-hist-badge jcm-hist-badge--queued";
    },
    formatRecipientsSummary: function (log) {
      if (!log || !log.recipients || !log.recipients.length) {
        return "0 recipients";
      }
      if (log.recipients.length === 1) {
        var only = log.recipients[0];
        return only.name || only.email;
      }
      return log.recipients.length + " recipients";
    },
    toggleExpand: function (id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    goPage: function (nextPage) {
      this.page = nextPage;
      this.fetchLogs();
    },
    fetchLogs: async function () {
      if (!this.partnerToken) {
        return;
      }
      this.isLoading = true;
      try {
        var response = await this.$apollo.query({
          query: gql`
            query CurrentPartnerJointCommitteeEmailLogs(
              $partner_token: String!
              $first: Int
              $page: Int
              $search: String
            ) {
              currentPartnerJointCommitteeEmailLogs(
                partner_token: $partner_token
                first: $first
                page: $page
                search: $search
              ) {
                error
                message
                paginatorInfo {
                  currentPage
                  lastPage
                  total
                  firstItem
                }
                data {
                  id
                  uuid
                  subject
                  status
                  body_html
                  recipient_count
                  sent_at
                  failed_at
                  last_error
                  created_at
                  owner_name
                  owner_email
                  recipients {
                    id
                    email
                    name
                    delivery_status
                    delivery_error
                    delivered_at
                  }
                }
              }
            }
          `,
          variables: {
            partner_token: this.partnerToken,
            first: this.first,
            page: this.page,
            search: this.search ? this.search : null,
          },
          fetchPolicy: "network-only",
        });

        var payload =
          response.data && response.data.currentPartnerJointCommitteeEmailLogs
            ? response.data.currentPartnerJointCommitteeEmailLogs
            : null;

        if (!payload || payload.error) {
          this.logs = [];
          this.paginatorInfo = null;
          return;
        }

        this.logs = payload.data || [];
        this.paginatorInfo = payload.paginatorInfo || null;
      } catch (error) {
        console.error("Failed to load Joint Committee email logs", error);
        this.logs = [];
        this.paginatorInfo = null;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.jcm-hist__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.jcm-hist__search {
  position: relative;
  flex: 1;
  min-width: 12rem;
}
.jcm-hist__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.75rem;
}
.jcm-hist__search-input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  outline: none;
  background: #fff;
}
.jcm-hist__search-input:focus {
  border-color: #3b82f6;
}
.jcm-hist__refresh {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
}
.jcm-hist__refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.jcm-hist__state {
  padding: 1.25rem 1rem;
  text-align: center;
  font-size: 0.8125rem;
  color: #94a3b8;
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
  background: #fff;
}
.jcm-hist__list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.jcm-hist-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #fff;
  overflow: hidden;
}
.jcm-hist-card__head {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.jcm-hist-card__head:hover {
  background: #f8fafc;
}
.jcm-hist-card__subject {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
}
.jcm-hist-card__meta {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
}
.jcm-hist-card__body {
  padding: 0 1rem 1rem;
  border-top: 1px solid #f1f5f9;
}
.jcm-hist-card__block {
  margin-top: 0.875rem;
}
.jcm-hist-card__label {
  margin: 0 0 0.5rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
}
.jcm-hist-card__content {
  padding: 0.75rem;
  border-radius: 0.5rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 0.8125rem;
  color: #334155;
  line-height: 1.5;
}
.jcm-hist-card__error {
  margin: 0.5rem 0 0;
  font-size: 0.75rem;
  color: #dc2626;
}
.jcm-hist-recipients {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.jcm-hist-recipients__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.625rem;
  border-radius: 0.5rem;
  background: #f8fafc;
}
.jcm-hist-recipients__name {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111827;
}
.jcm-hist-recipients__email {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
}
.jcm-hist-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: capitalize;
  flex-shrink: 0;
}
.jcm-hist-badge--sent {
  background: #d1fae5;
  color: #047857;
}
.jcm-hist-badge--failed {
  background: #fee2e2;
  color: #b91c1c;
}
.jcm-hist-badge--partial {
  background: #fef3c7;
  color: #b45309;
}
.jcm-hist-badge--queued {
  background: #dbeafe;
  color: #1d4ed8;
}
.jcm-hist__pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
}
.jcm-hist__page-btn {
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #fff;
  cursor: pointer;
}
.jcm-hist__page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.jcm-hist__page-label {
  font-size: 0.75rem;
  color: #64748b;
}
</style>
